import { fetchQuery, graphql, type Environment } from 'react-relay';

import type { ReportingLines } from '@atlaskit/teams-client/reporting-lines-client/utils/types';
import { PlatformSiteAri } from '@atlassian/ari';

import type { getReportingLinesFromKnowledgeDiscoveryQuery } from './__generated__/getReportingLinesFromKnowledgeDiscoveryQuery.graphql';
import { mapKnowledgeDiscoveryReportingLines } from './mapKnowledgeDiscoveryReportingLines';

export type KnowledgeDiscoveryContext = {
	/** Cloud id used to build the site ARI required by the query. */
	cloudId: string;
	/** Org id required by the query. */
	orgId: string;
};

/**
 * Builds the site ARI that the KnowledgeDiscovery `reportingLines(siteId:)` arg
 * requires (it expects an ARI, not the raw cloudId).
 */
export const buildSiteAri = (cloudId: string): string =>
	PlatformSiteAri.create({ siteId: cloudId }).toString();

/**
 * Imperatively fetches reporting lines via the KnowledgeDiscovery
 * `reportingLines` GraphQL query and maps the result into the `ReportingLines`
 * shape used by the reporting-lines store.
 *
 * @returns The mapped reporting lines, or `undefined` when the response does not
 * contain the successful union branch / has no root user.
 */
export const getReportingLinesFromKnowledgeDiscovery = async (
	environment: Environment,
	userId: string,
	{ cloudId, orgId }: KnowledgeDiscoveryContext,
): Promise<ReportingLines | undefined> => {
	// Fields below are consumed via `mapKnowledgeDiscoveryReportingLines`, which
	// maps the whole payload into the store's `ReportingLines` shape. The Relay
	// lint rule cannot follow the mapper, so it is disabled for this query.
	/* eslint-disable @atlassian/relay/unused-fields */
	const data = await fetchQuery<getReportingLinesFromKnowledgeDiscoveryQuery>(
		environment,
		graphql`
			query getReportingLinesFromKnowledgeDiscoveryQuery(
				$aaid: String!
				$orgId: String!
				$siteId: String!
			) {
				knowledgeDiscovery {
					reportingLines(aaid: $aaid, orgId: $orgId, siteId: $siteId)
						@optIn(to: "KnowledgeDiscovery Get reporting lines") {
						__typename
						... on KnowledgeDiscoveryReportingLines {
							user {
								accountIdentifier
								identifierType
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									accountStatus
									extendedProfile {
										jobTitle
									}
								}
							}
							managers {
								accountIdentifier
								identifierType
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									accountStatus
									extendedProfile {
										jobTitle
									}
								}
							}
							reports {
								accountIdentifier
								identifierType
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									accountStatus
									extendedProfile {
										jobTitle
									}
								}
							}
							peers {
								accountIdentifier
								identifierType
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									accountStatus
									extendedProfile {
										jobTitle
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			aaid: userId,
			orgId,
			siteId: buildSiteAri(cloudId),
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();
	/* eslint-enable @atlassian/relay/unused-fields */

	const reportingLines = data?.knowledgeDiscovery?.reportingLines;

	if (reportingLines?.__typename !== 'KnowledgeDiscoveryReportingLines') {
		return undefined;
	}

	return mapKnowledgeDiscoveryReportingLines(reportingLines);
};
